import {
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CInput,
  CLabel,
  CPagination,
  CRow,
  CSelect,
} from "@coreui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import Datetime from "react-datetime";
import { useSelector } from "react-redux";
import { createItem, getItem, getList, updateItem } from "../../api/generics";
import { TREASURY_MANAGE, TREASURY_MOVEMENTS } from "../../auth/permissions";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import {
  MOVEMENT_STATUSES,
  MOVEMENT_STATUS_ANNULLED,
  MOVEMENT_STATUS_ASSIGNED,
  MOVEMENT_STATUS_DISCARDED,
  MOVEMENT_STATUS_PENDING,
  MOVEMENT_SOURCE_MANUAL,
  TreasuryMovement,
  TreasuryMovementsTotals,
} from "../../models/treasury";
import { RootState } from "../../store";
import { formatDateTime } from "../../utils/dates";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import TreasuryAssignModal from "./TreasuryAssignModal";
import TreasuryConfirmModal from "./TreasuryConfirmModal";
import TreasuryMovementForm from "./TreasuryMovementForm";
import TreasuryMovementLogModal from "./TreasuryMovementLogModal";

const ITEMS_PER_PAGE = 50;

interface Filters {
  movementStatus: string;
  search: string;
  startDate: string | moment.Moment;
  endDate: string | moment.Moment;
}

// Duenos (TREASURY_MANAGE) aceptan, descartan y ven totales. Los operadores
// (solo TREASURY_MOVEMENTS) cargan y editan pendientes; el backend tambien
// les rechaza el resto.
const TreasuryMovementList = () => {
  const permissions = useSelector((state: RootState) => state.user.permissions);
  const canManage =
    permissions !== undefined && permissions.indexOf(TREASURY_MANAGE) >= 0;
  const canLoad =
    permissions !== undefined && permissions.indexOf(TREASURY_MOVEMENTS) >= 0;

  const fields = [
    ...(canManage ? [{ key: "select", label: "", filter: false }] : []),
    { key: "id", label: "Nro." },
    { key: "movementDate", label: "Fecha" },
    { key: "description", label: "Origen" },
    { key: "reason", label: "Observación" },
    { key: "amount", label: "Monto", _classes: ["text-right"] },
    { key: "account", label: "Cuenta" },
    { key: "status", label: "Estado", _classes: ["text-center"] },
    { key: "createdByUserName", label: "Cargado por" },
    { key: "actions", label: "Acciones", _classes: ["text-center"] },
  ];

  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );
  const [filters, setFilters] = useState<Filters>({
    movementStatus: MOVEMENT_STATUS_PENDING,
    search: "",
    startDate: "",
    endDate: "",
  });
  const [movements, setMovements] = useState<TreasuryMovement[]>([]);
  const [totals, setTotals] = useState<TreasuryMovementsTotals | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [assigning, setAssigning] = useState<TreasuryMovement[] | undefined>(
    undefined
  );
  const [processing, setProcessing] = useState(false);
  // show sin movimiento = alta; con movimiento = edicion.
  const [form, setForm] = useState<{
    show: boolean;
    movement?: TreasuryMovement;
  }>({ show: false });
  const [viewingLog, setViewingLog] = useState<TreasuryMovement | undefined>(
    undefined
  );
  const [discarding, setDiscarding] = useState<TreasuryMovement[] | undefined>(
    undefined
  );
  const [reopening, setReopening] = useState<TreasuryMovement[] | undefined>(
    undefined
  );

  const buildParams = () => {
    const params = new Map<string, string | undefined>();
    params.set("company_id", companyId?.toString());
    if (filters.movementStatus !== "") {
      params.set("movement_status", filters.movementStatus);
    }
    if (filters.search !== "") {
      params.set("search", filters.search);
    }
    if (moment.isMoment(filters.startDate)) {
      params.set("start_date", filters.startDate.toISOString());
    }
    if (moment.isMoment(filters.endDate)) {
      params.set("end_date", filters.endDate.toISOString());
    }
    return params;
  };

  const fetchMovements = async (page: number) => {
    // CPagination "corrige" la pagina activa a `pages` cuando hay menos
    // paginas que la actual; con 0 resultados eso es la pagina 0.
    page = Math.max(1, page);
    setLoading(true);
    const params = buildParams();
    const [movementsStatus, totalsStatus] = await Promise.all([
      getList<TreasuryMovement>(
        "/treasury/movements/",
        ITEMS_PER_PAGE,
        ITEMS_PER_PAGE * (page - 1),
        params
      ),
      // Los totales dejan deducir saldos: solo para duenos.
      canManage
        ? getItem<TreasuryMovementsTotals>(
            "/treasury/movements/totals/",
            params
          )
        : Promise.resolve(undefined),
    ]);
    if (movementsStatus.status === SUCCESS && movementsStatus.data) {
      setMovements(movementsStatus.data.items);
      setTotalPages(
        Math.max(1, Math.ceil(movementsStatus.data.count / ITEMS_PER_PAGE))
      );
      setCurrentPage(page);
      setSelectedIds([]);
    } else {
      warningAlert(movementsStatus.detail ?? "Error desconocido");
    }
    if (totalsStatus !== undefined && totalsStatus.status === SUCCESS) {
      setTotals(totalsStatus.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMovements(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La bandeja solo trae lo cargado a mano; los automaticos del sistema
  // anterior no llegan y el backend rechaza cualquier accion sobre ellos.
  const selectable = (movement: TreasuryMovement) =>
    canManage &&
    (movement.status === MOVEMENT_STATUS_PENDING ||
      movement.status === MOVEMENT_STATUS_ASSIGNED ||
      movement.status === MOVEMENT_STATUS_DISCARDED);

  const selectedMovements = movements.filter(
    (movement) => selectedIds.indexOf(movement.id) >= 0
  );
  // Cada accion en lote toma solo los seleccionados a los que aplica.
  // Aceptar incluye a los ya aceptados: con otra cuenta se reasignan y los
  // que ya estan en la cuenta elegida se saltean.
  const selectedToAccept = selectedMovements;
  const selectedToDiscard = selectedMovements.filter(
    (m) => m.status !== MOVEMENT_STATUS_DISCARDED
  );
  const selectedToReopen = selectedMovements.filter(
    (m) =>
      m.status === MOVEMENT_STATUS_ASSIGNED ||
      m.status === MOVEMENT_STATUS_DISCARDED
  );

  const editable = (movement: TreasuryMovement) =>
    canLoad &&
    movement.status === MOVEMENT_STATUS_PENDING &&
    movement.sourceType === MOVEMENT_SOURCE_MANUAL;

  const toggleSelected = (id: number) => {
    setSelectedIds(
      selectedIds.indexOf(id) >= 0
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id]
    );
  };

  const toggleAll = () => {
    const selectableIds = movements.filter(selectable).map((m) => m.id);
    setSelectedIds(
      selectedIds.length === selectableIds.length ? [] : selectableIds
    );
  };

  const onRequestDone = async (
    request: Promise<{ status: string; detail?: string }>,
    message: string
  ) => {
    setProcessing(true);
    const requestStatus = await request;
    setProcessing(false);
    if (requestStatus.status !== SUCCESS) {
      errorAlert(requestStatus.detail ?? "Ha ocurrido un error!!");
      return false;
    }
    successAlert(message);
    fetchMovements(currentPage);
    return true;
  };

  // accountId undefined (solo en lote): cada uno a su cuenta destino.
  const onAssignConfirm = async (accountId: number | undefined) => {
    if (assigning === undefined) {
      return;
    }
    const request =
      assigning.length === 1
        ? updateItem(`/treasury/movements/${assigning[0].id}/assign/`, {
            accountId,
          })
        : createItem("/treasury/movements/bulk_assign/", {
            ids: assigning.map((m) => m.id),
            accountId,
          });
    const ok = await onRequestDone(request, "Movimientos aceptados");
    if (ok) {
      setAssigning(undefined);
    }
  };

  const onDiscardConfirm = async () => {
    const toDiscard = discarding;
    if (toDiscard === undefined) {
      return;
    }
    const request =
      toDiscard.length === 1
        ? updateItem(`/treasury/movements/${toDiscard[0].id}/discard/`, {})
        : createItem("/treasury/movements/bulk_discard/", {
            ids: toDiscard.map((m) => m.id),
          });
    if (await onRequestDone(request, "Movimientos descartados")) {
      setDiscarding(undefined);
    }
  };

  const onReopenConfirm = async () => {
    const toReopen = reopening;
    if (toReopen === undefined) {
      return;
    }
    const request =
      toReopen.length === 1
        ? updateItem(`/treasury/movements/${toReopen[0].id}/reopen/`, {})
        : createItem("/treasury/movements/bulk_reopen/", {
            ids: toReopen.map((m) => m.id),
          });
    if (await onRequestDone(request, "Movimientos vueltos a pendiente")) {
      setReopening(undefined);
    }
  };

  const onDateChange =
    (key: "startDate" | "endDate") => (value: string | moment.Moment) => {
      setFilters({
        ...filters,
        [key]: moment.isMoment(value) && value.isValid() ? value : "",
      });
    };

  const assigningTotal = (assigning ?? []).reduce(
    (sum, m) => sum + Number(m.amount),
    0
  );
  const suggestedAccountId =
    assigning !== undefined && assigning.length === 1
      ? assigning[0].suggestedAccountId
      : undefined;

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>Movimientos de Cuentas</h3>
              </div>
              {canLoad ? (
                <div className="p-2">
                  <CButton
                    color="success"
                    onClick={() => setForm({ show: true })}
                  >
                    <i className="fa fa-plus"></i>
                    &nbsp; Nuevo movimiento
                  </CButton>
                </div>
              ) : (
                <></>
              )}
            </CCardHeader>
            <CCardBody>
              <CRow className="mb-2">
                <CCol md={1}>
                  <CLabel className="mt-2">Estado:</CLabel>
                </CCol>
                <CCol md={3}>
                  <CSelect
                    value={filters.movementStatus}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setFilters({ ...filters, movementStatus: e.target.value })
                    }
                  >
                    <option value="">Todos</option>
                    {Array.from(MOVEMENT_STATUSES.entries()).map(
                      ([value, status]) => (
                        <option key={value} value={value}>
                          {status.label}
                        </option>
                      )
                    )}
                  </CSelect>
                </CCol>
                <CCol md={1}>
                  <CLabel className="mt-2">Desde:</CLabel>
                </CCol>
                <CCol md={3}>
                  <Datetime
                    onChange={onDateChange("startDate")}
                    value={filters.startDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
                <CCol md={1}>
                  <CLabel className="mt-2">Hasta:</CLabel>
                </CCol>
                <CCol md={3}>
                  <Datetime
                    onChange={onDateChange("endDate")}
                    value={filters.endDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CCol md={4}>
                  <CInput
                    type="text"
                    placeholder="Buscar por origen o Nro."
                    value={filters.search}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter") fetchMovements(1);
                    }}
                  />
                </CCol>
                <CCol md={2}>
                  <CButton
                    color="primary"
                    block
                    onClick={() => fetchMovements(1)}
                  >
                    Buscar
                  </CButton>
                </CCol>
              </CRow>
              {totals !== undefined ? (
                <CRow className="mb-3">
                  <CCol>
                    <b>{totals.count}</b> movimientos &nbsp;|&nbsp; Ingresos:{" "}
                    <b className="text-success">
                      {formatToCurrency(Number(totals.totalIn), PYG)}
                    </b>{" "}
                    &nbsp;|&nbsp; Egresos:{" "}
                    <b className="text-danger">
                      {formatToCurrency(Number(totals.totalOut), PYG)}
                    </b>{" "}
                    &nbsp;|&nbsp; Neto:{" "}
                    <b>{formatToCurrency(Number(totals.net), PYG)}</b>
                  </CCol>
                </CRow>
              ) : (
                <></>
              )}
              {canManage ? (
                <CRow className="mb-2">
                  <CCol>
                    <CButtonGroup>
                      <CButton
                        color="primary"
                        disabled={selectedToAccept.length === 0 || processing}
                        onClick={() => setAssigning(selectedToAccept)}
                      >
                        Aceptar / reasignar seleccionados (
                        {selectedToAccept.length})
                      </CButton>
                      <CButton
                        color="secondary"
                        disabled={selectedToDiscard.length === 0 || processing}
                        onClick={() => setDiscarding(selectedToDiscard)}
                      >
                        Descartar seleccionados ({selectedToDiscard.length})
                      </CButton>
                      <CButton
                        color="warning"
                        className="text-white"
                        disabled={selectedToReopen.length === 0 || processing}
                        onClick={() => setReopening(selectedToReopen)}
                      >
                        Volver a pendiente ({selectedToReopen.length})
                      </CButton>
                    </CButtonGroup>
                  </CCol>
                </CRow>
              ) : (
                <></>
              )}
              <CDataTable
                noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                fields={fields}
                columnHeaderSlot={{
                  select: (
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.length > 0 &&
                        selectedIds.length ===
                          movements.filter(selectable).length
                      }
                      onChange={toggleAll}
                    />
                  ),
                }}
                items={movements}
                striped
                border
                loading={loading}
                responsive
                scopedSlots={{
                  select: (item: TreasuryMovement) => (
                    <td className="text-center">
                      {selectable(item) ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.indexOf(item.id) >= 0}
                          onChange={() => toggleSelected(item.id)}
                        />
                      ) : (
                        <></>
                      )}
                    </td>
                  ),
                  id: (item: TreasuryMovement) => <td>#{item.id}</td>,
                  movementDate: (item: TreasuryMovement) => (
                    <td>{formatDateTime(new Date(item.movementDate))}</td>
                  ),
                  description: (item: TreasuryMovement) => (
                    <td>
                      {item.description ?? "-"}
                    </td>
                  ),
                  // En los ajustes manuales el motivo ya es el origen.
                  reason: (item: TreasuryMovement) => (
                    <td>
                      {item.reason && item.reason !== item.description
                        ? item.reason
                        : "-"}
                    </td>
                  ),
                  createdByUserName: (item: TreasuryMovement) => (
                    <td>
                      {item.createdByUserName ?? "-"}
                    </td>
                  ),
                  amount: (item: TreasuryMovement) => (
                    <td
                      className={
                        "text-right " +
                        (Number(item.amount) < 0
                          ? "text-danger"
                          : "text-success")
                      }
                    >
                      <b>{formatToCurrency(Number(item.amount), PYG)}</b>
                    </td>
                  ),
                  status: (item: TreasuryMovement) => {
                    const status = MOVEMENT_STATUSES.get(item.status);
                    return (
                      <td className="text-center">
                        <CBadge color={status?.color ?? "light"}>
                          {status?.label ?? item.status}
                        </CBadge>
                      </td>
                    );
                  },
                  account: (item: TreasuryMovement) => (
                    <td>
                      {item.accountName ??
                        (item.status === MOVEMENT_STATUS_PENDING &&
                        item.suggestedAccountName ? (
                          <>
                            {item.suggestedAccountName}{" "}
                            <small className="text-muted">(destino)</small>
                          </>
                        ) : (
                          "-"
                        ))}
                    </td>
                  ),
                  actions: (item: TreasuryMovement) => {
                    const actionable =
                      canManage && item.status !== MOVEMENT_STATUS_ANNULLED;
                    return (
                      <td className="text-right">
                        <CButtonGroup>
                          {editable(item) ? (
                            <CButton
                              color="info"
                              title="Editar"
                              disabled={processing}
                              onClick={() =>
                                setForm({ show: true, movement: item })
                              }
                            >
                              <i className="fa fa-pencil"></i>
                            </CButton>
                          ) : (
                            <></>
                          )}
                          {actionable ? (
                            <CButton
                              color="primary"
                              title={
                                item.status === MOVEMENT_STATUS_ASSIGNED
                                  ? "Reasignar"
                                  : "Aceptar"
                              }
                              disabled={processing}
                              onClick={() => setAssigning([item])}
                            >
                              <i
                                className={
                                  item.status === MOVEMENT_STATUS_ASSIGNED
                                    ? "fa fa-exchange"
                                    : "fa fa-check"
                                }
                              ></i>
                            </CButton>
                          ) : (
                            <></>
                          )}
                          {actionable &&
                          (item.status === MOVEMENT_STATUS_ASSIGNED ||
                            item.status === MOVEMENT_STATUS_DISCARDED) ? (
                            <CButton
                              color="warning"
                              className="text-white"
                              title={
                                item.status === MOVEMENT_STATUS_ASSIGNED
                                  ? "Revertir aprobación"
                                  : "Volver a pendiente"
                              }
                              disabled={processing}
                              onClick={() => setReopening([item])}
                            >
                              <i className="fa fa-undo"></i>
                            </CButton>
                          ) : (
                            <></>
                          )}
                          {actionable &&
                          item.status !== MOVEMENT_STATUS_DISCARDED ? (
                            <CButton
                              color="secondary"
                              title="Descartar"
                              disabled={processing}
                              onClick={() => setDiscarding([item])}
                            >
                              <i className="fa fa-ban"></i>
                            </CButton>
                          ) : (
                            <></>
                          )}
                          <CButton
                            color="light"
                            title="Historial"
                            onClick={() => setViewingLog(item)}
                          >
                            <i className="fa fa-history"></i>
                          </CButton>
                        </CButtonGroup>
                      </td>
                    );
                  },
                }}
              />
              <div className="d-flex justify-content-center">
                <CPagination
                  pages={totalPages}
                  activePage={currentPage}
                  onActivePageChange={(page: number) => fetchMovements(page)}
                  className={totalPages < 2 ? "d-none" : ""}
                />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <TreasuryConfirmModal
        show={discarding !== undefined}
        title="Descartar"
        color="warning"
        confirmLabel="Descartar"
        message={
          discarding !== undefined && discarding.length === 1
            ? discarding[0].status === MOVEMENT_STATUS_ASSIGNED
              ? `¿Descartar el movimiento? Ya no se imputará a ${discarding[0].accountName}.`
              : "¿Descartar el movimiento? No se imputará a ninguna cuenta."
            : (discarding ?? []).some(
                (m) => m.status === MOVEMENT_STATUS_ASSIGNED
              )
            ? `¿Descartar ${discarding?.length ?? 0} movimientos? Los asignados ya no se imputarán a su cuenta.`
            : `¿Descartar ${discarding?.length ?? 0} movimientos? No se imputarán a ninguna cuenta.`
        }
        onCancel={() => setDiscarding(undefined)}
        onConfirm={onDiscardConfirm}
      />
      <TreasuryConfirmModal
        show={reopening !== undefined}
        title="Volver a pendiente"
        color="warning"
        confirmLabel="Volver a pendiente"
        message={
          reopening !== undefined && reopening.length === 1 ? (
            reopening[0].status === MOVEMENT_STATUS_ASSIGNED ? (
              <>
                ¿Revertir la aprobación? Se descuenta de{" "}
                <b>{reopening[0].accountName}</b> y vuelve a pendiente.
              </>
            ) : (
              "¿Volver el movimiento descartado a pendiente?"
            )
          ) : (reopening ?? []).some(
              (m) => m.status === MOVEMENT_STATUS_ASSIGNED
            ) ? (
            `¿Volver ${reopening?.length ?? 0} movimientos a pendiente? Los aceptados se descuentan de su cuenta.`
          ) : (
            `¿Volver ${reopening?.length ?? 0} movimientos a pendiente?`
          )
        }
        onCancel={() => setReopening(undefined)}
        onConfirm={onReopenConfirm}
      />
      <TreasuryMovementForm
        show={form.show}
        movement={form.movement}
        onCancel={() => setForm({ show: false })}
        onSuccess={() => {
          successAlert(
            form.movement === undefined
              ? "Movimiento registrado"
              : "Movimiento modificado"
          );
          setForm({ show: false });
          fetchMovements(currentPage);
        }}
      />
      <TreasuryMovementLogModal
        movement={viewingLog}
        onClose={() => setViewingLog(undefined)}
      />
      <TreasuryAssignModal
        show={assigning !== undefined}
        movementsCount={assigning?.length ?? 0}
        totalAmount={assigningTotal}
        suggestedAccountId={suggestedAccountId}
        allowOwnDestination={(assigning?.length ?? 0) > 1}
        missingDestinationCount={
          (assigning ?? []).filter((m) => m.suggestedAccountId === undefined)
            .length
        }
        onCancel={() => setAssigning(undefined)}
        onConfirm={onAssignConfirm}
      />
    </>
  );
};

export default TreasuryMovementList;
